import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  ToolbarPropsOverrides,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import IncExportButton from 'components/inc-export-button/inc-export-button.component';
import { SearchParam } from 'components/search/search.context';
import {
  makeQueryParam,
  useSearchParamsContext,
  useSearchQueryContext,
} from 'components/search/search.hooks';
import omit from 'lodash.omit';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { downloadCSV } from 'util/download-csv.utils';

const makeParamsBody = (searchParams: { [key: string]: SearchParam }) => {
  const queryParams = {
    startDate: makeQueryParam(searchParams.startdate, searchParams),
    endDate: makeQueryParam(searchParams.enddate, searchParams),
    storeIds: makeQueryParam(searchParams.storeIds, searchParams).split(','),
    offerType: undefined,
    offerIds: undefined,
    granularity: undefined,
    dataType: undefined,
  };

  if (searchParams.offerIds) {
    queryParams.offerIds = makeQueryParam(
      searchParams.offerIds,
      searchParams,
    ).split(',');
  }

  if (searchParams.offertype) {
    queryParams.offerType = makeQueryParam(
      searchParams.offertype,
      searchParams,
    );
  }

  if (searchParams.granularity) {
    queryParams.granularity = makeQueryParam(
      searchParams.granularity,
      searchParams,
    );
  }

  if (searchParams.datatype) {
    queryParams.dataType = makeQueryParam(searchParams.datatype, searchParams);
  }

  if (searchParams.offerdatatype) {
    queryParams.dataType = makeQueryParam(
      searchParams.offerdatatype,
      searchParams,
    );
  }

  return queryParams;
};

const LoyaltyDataGridToolbar = ({ toolbar }: ToolbarPropsOverrides) => {
  const { enqueueSnackbar } = useSnackbar();
  const { searchParams } = useSearchParamsContext();
  const {
    queryName,
    filteredSearchParams: fsp,
    query,
    fileName,
    includeColumnsField,
  } = toolbar.export;
  const { queryState } = useSearchQueryContext(queryName);
  const filteredSearchParams = omit(searchParams, fsp);
  const api = useGridApiContext();

  const [exportCSV, { isLoading, isSuccess, data }] = query();

  const handleExportCSV = async () => {
    await exportCSV({
      columns: includeColumnsField
        ? (api.current.getVisibleColumns().map(col => col.field) as string[])
        : undefined,
      ...makeParamsBody(filteredSearchParams),
    });
  };

  useEffect(() => {
    if (isSuccess) {
      if (data.status === 'DOWNLOADED') {
        downloadCSV(data.data.REPORT, fileName);
      } else {
        enqueueSnackbar(data.responseMessage, {
          severity: 'success',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        justifyContent: includeColumnsField ? 'space-between' : 'flex-end',
      }}
    >
      {includeColumnsField && <GridToolbarColumnsButton />}
      <IncExportButton
        label="Export"
        onClick={handleExportCSV}
        data-testid="LoyaltyDataGridToolbar.IncExportButton"
        disabled={queryState.isFetching}
        loading={isLoading}
        loadingPosition="start"
      />
    </GridToolbarContainer>
  );
};

export default LoyaltyDataGridToolbar;
